import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Leaf, Sparkles, Heart, Wind, Dumbbell, Activity, Video, MessageCircle,
  ArrowRight, MapPin, Pencil, Save, RotateCcw, X, Plus, Trash2,
  Utensils, Truck, Smartphone, AppWindow, ChefHat, Dna, Scale, Zap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const SLUG = "academy-benessere-integrato";

const ICONS = { Leaf, Sparkles, Heart, Wind, Dumbbell, Activity, Utensils, Truck, Smartphone, AppWindow, ChefHat, Dna, Scale, Zap };

// Small editable text component (only when editing)
const T = ({ editing, value, onChange, as = "span", className = "", multiline = false, placeholder = "" }) => {
  if (!editing) {
    const Tag = as;
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: (value || "").replace(/\n/g, "<br/>") }} />;
  }
  if (multiline) {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-amber-400`} placeholder={placeholder} rows={3} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${className} bg-white/95 text-slate-900 border-2 border-amber-400`} placeholder={placeholder} />;
};

const AcademyBenessereLandingPage = () => {
  const { user, token } = useAuth();
  const isAdmin = user && ["admin", "direzione"].includes(user.role);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/landing/${SLUG}`).then((r) => setData(r.data)).catch(() => toast.error("Errore caricamento contenuti"));
  }, []);

  const set = (path, val) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i].includes("[") ? keys[i].replace(/\[(\d+)\]/g, ".$1").split(".") : [keys[i]];
        k.forEach(kk => { ref = ref[kk]; });
      }
      const lastKey = keys[keys.length - 1];
      if (lastKey.includes("[")) {
        const [name, idx] = lastKey.split(/[\[\]]/).filter(Boolean);
        ref[name][idx] = val;
      } else {
        ref[lastKey] = val;
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing/${SLUG}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Landing salvata");
      setEditing(false);
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare i contenuti di default? Tutte le modifiche saranno perse.")) return;
    try {
      const r = await axios.post(`${API}/landing/${SLUG}/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
      toast.success("Contenuti ripristinati");
      setEditing(false);
    } catch (e) {
      toast.error("Errore ripristino");
    }
  };

  const uploadImage = async (fieldPath) => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = "image/*";
    inp.onchange = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      // client-side compression to base64 (max 1600px side, quality 0.85)
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (ev) => { img.src = ev.target.result; };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSide = 1600;
        let { width, height } = img;
        if (Math.max(width, height) > maxSide) {
          if (width >= height) { height = Math.round(height * (maxSide / width)); width = maxSide; }
          else { width = Math.round(width * (maxSide / height)); height = maxSide; }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUri = canvas.toDataURL("image/jpeg", 0.85);
        set(fieldPath, dataUri);
        toast.success("Immagine caricata");
      };
      reader.readAsDataURL(f);
    };
    inp.click();
  };

  const copyPublicLink = () => {
    const url = window.location.origin + "/academy-benessere-integrato";
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiato: " + url));
  };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#faf7f2] text-[#2d3a2e]">Caricamento…</div>;
  }

  const h = data.hero || {};
  const vis = data.vision || {};
  const met = data.metodo || {};
  const perc = data.percorsi || { items: [] };
  const ita = data.ita318 || {};
  const prod = data.prodotti || { punti: [] };
  const ret = data.retreat || {};
  const cta = data.cta_finale || { buttons: [] };
  const foot = data.footer || {};
  const wa = data.whatsapp || {};
  const nut = data.nutrizione || { servizi: [], obiettivi: [], icone: [] };
  const bio = data.biofitness || { unisce: [], obiettivi: [] };
  const eco = data.ecosistema || { orbitals: [] };
  const srv = data.servizi_esclusivi || { items: [] };

  return (
    <div className="min-h-screen bg-[#faf7f2] text-[#3a3730]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="academy-benessere-page">
      {/* ADMIN TOOLBAR */}
      {isAdmin && (
        <div className="sticky top-0 z-[60] bg-[#2d3a2e] text-white px-4 py-2 flex items-center justify-between flex-wrap gap-2 border-b-2 border-[#d4b678]" data-testid="admin-toolbar">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[#d4b678] font-semibold tracking-wider uppercase">Admin</span>
            <span className="text-white/70">Landing: Academy Benessere</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={copyPublicLink} className="text-slate-900" data-testid="btn-copy-link">
              Copia link pubblico
            </Button>
            {!editing ? (
              <Button size="sm" onClick={() => setEditing(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="btn-edit">
                <Pencil className="w-4 h-4 mr-1" /> Modifica
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save">
                  <Save className="w-4 h-4 mr-1" /> {saving ? "Salvo…" : "Salva"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-slate-900" data-testid="btn-cancel">
                  <X className="w-4 h-4 mr-1" /> Annulla
                </Button>
                <Button size="sm" variant="outline" onClick={resetDefault} className="text-red-600" data-testid="btn-reset">
                  <RotateCcw className="w-4 h-4 mr-1" /> Ripristina default
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${h.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2d3a2e]/80 via-[#3d4a3e]/60 to-[#1a2e20]/85" />
        {editing && (
          <button onClick={() => uploadImage("hero.image")} className="absolute top-4 right-4 z-20 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-3 py-2 rounded-full font-semibold" data-testid="hero-img-btn">Cambia immagine hero</button>
        )}
        <div className="relative z-10 h-full flex items-center px-8 md:px-16 lg:px-24">
          <div className="max-w-3xl w-full">
            <T editing={editing} value={h.eyebrow} onChange={(v) => set("hero.eyebrow", v)}
               className="block text-[#d4b678] text-xs md:text-sm tracking-[6px] uppercase mb-6 font-semibold"
               as="p" />
            <h1 className="text-white text-5xl md:text-7xl lg:text-8xl font-light leading-[1.05] mb-8 drop-shadow-2xl">
              <T editing={editing} value={h.title_line1} onChange={(v) => set("hero.title_line1", v)} as="span" />
              <br />
              <em className="text-[#d4b678] font-normal not-italic">
                <T editing={editing} value={h.title_line2} onChange={(v) => set("hero.title_line2", v)} as="span" />
              </em>
            </h1>
            <T editing={editing} value={h.subtitle} onChange={(v) => set("hero.subtitle", v)} multiline
               className="block text-white/95 text-lg md:text-2xl leading-relaxed mb-10 max-w-2xl font-light" as="p" />
            <div className="flex flex-wrap gap-4">
              <a href="#percorsi" className="inline-flex items-center gap-2 bg-[#d4b678] hover:bg-[#c4a668] text-[#2d3a2e] px-8 py-4 rounded-full font-semibold text-sm tracking-wider uppercase transition-all hover:scale-105 shadow-xl" data-testid="cta-corsi">
                <T editing={editing} value={h.cta_primary} onChange={(v) => set("hero.cta_primary", v)} as="span" /> <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#contatti" className="inline-flex items-center gap-2 border-2 border-white/60 hover:bg-white/15 text-white px-8 py-4 rounded-full font-semibold text-sm tracking-wider uppercase transition-all" data-testid="cta-academy">
                <T editing={editing} value={h.cta_secondary} onChange={(v) => set("hero.cta_secondary", v)} as="span" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-24 text-center">
        <T editing={editing} value={vis.eyebrow} onChange={(v) => set("vision.eyebrow", v)}
           className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-6" as="p" />
        <h2 className="text-4xl md:text-5xl font-light text-[#2d3a2e] mb-8 leading-tight">
          <T editing={editing} value={vis.title_pt1} onChange={(v) => set("vision.title_pt1", v)} as="span" /><br />
          <em className="text-[#7a8a70] not-italic">
            <T editing={editing} value={vis.title_pt2} onChange={(v) => set("vision.title_pt2", v)} as="span" />
          </em>
        </h2>
        <T editing={editing} value={vis.description} onChange={(v) => set("vision.description", v)} multiline
           className="block text-lg md:text-xl text-[#5a5750] leading-[1.9] font-light max-w-3xl mx-auto" as="p" />
        <div className="w-24 h-[1px] bg-[#d4b678] mx-auto mt-12"></div>
      </section>

      {/* METODO */}
      <section className="bg-white py-24 px-6" data-testid="metodo">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <T editing={editing} value={met.eyebrow} onChange={(v) => set("metodo.eyebrow", v)}
               className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-4" as="p" />
            <T editing={editing} value={met.title} onChange={(v) => set("metodo.title", v)}
               className="block text-4xl md:text-5xl font-light text-[#2d3a2e]" as="h2" />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { key: "online", icon: Video, path: "metodo.online" },
              { key: "esperienziale", icon: MapPin, path: "metodo.esperienziale" },
            ].map(({ key, icon: Icon, path }) => {
              const block = met[key] || { punti: [] };
              return (
                <div key={key} className="bg-[#faf7f2] p-10 rounded-lg border border-[#e8e0d0]" data-testid={`metodo-${key}`}>
                  <Icon className="w-12 h-12 text-[#d4b678] mb-6" strokeWidth={1.2} />
                  <T editing={editing} value={block.title} onChange={(v) => set(`${path}.title`, v)}
                     className="block text-2xl md:text-3xl font-normal text-[#2d3a2e] mb-4" as="h3" />
                  <T editing={editing} value={block.intro} onChange={(v) => set(`${path}.intro`, v)}
                     className="block text-[#5a5750] leading-relaxed mb-4 font-light" as="p" />
                  <ul className="space-y-2 text-[#5a5750] font-light">
                    {(block.punti || []).map((p, i) => (
                      <li key={i} className="flex gap-3 items-start">
                        <span className="text-[#d4b678]">✦</span>
                        {editing ? (
                          <div className="flex-1 flex gap-2">
                            <Input value={p} onChange={(e) => {
                              const arr = [...block.punti]; arr[i] = e.target.value; set(`${path}.punti`, arr);
                            }} className="bg-white text-slate-900 border-2 border-amber-400 text-sm" />
                            <button onClick={() => { const arr = block.punti.filter((_, ii) => ii !== i); set(`${path}.punti`, arr); }} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ) : <span>{p}</span>}
                      </li>
                    ))}
                    {editing && (
                      <li><button onClick={() => set(`${path}.punti`, [...(block.punti || []), "Nuovo punto"])} className="text-[#7a8a70] text-xs flex items-center gap-1 hover:text-[#2d3a2e]"><Plus className="w-3 h-3" /> Aggiungi punto</button></li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PERCORSI */}
      <section id="percorsi" className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="percorsi">
        <div className="text-center mb-16">
          <T editing={editing} value={perc.eyebrow} onChange={(v) => set("percorsi.eyebrow", v)}
             className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-4" as="p" />
          <T editing={editing} value={perc.title} onChange={(v) => set("percorsi.title", v)}
             className="block text-4xl md:text-5xl font-light text-[#2d3a2e]" as="h2" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(perc.items || []).map((p, i) => {
            const Icon = ICONS[p.icon] || Leaf;
            return (
              <div key={i} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-[#e8e0d0] group relative" data-testid={`percorso-${i}`}>
                <div className="h-48 overflow-hidden bg-[#faf7f2] relative">
                  <img src={p.img} alt={p.titolo} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                  {editing && (
                    <button onClick={() => uploadImage(`percorsi.items[${i}].img`)} className="absolute top-2 right-2 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-2 py-1 rounded-full font-semibold">Cambia immagine</button>
                  )}
                </div>
                <div className="p-6">
                  <Icon className="w-9 h-9 text-[#d4b678] mb-3" strokeWidth={1.3} />
                  <T editing={editing} value={p.titolo} onChange={(v) => set(`percorsi.items[${i}].titolo`, v)}
                     className="block text-xl md:text-2xl font-normal text-[#2d3a2e] mb-3 leading-snug" as="h3" />
                  <T editing={editing} value={p.desc} onChange={(v) => set(`percorsi.items[${i}].desc`, v)} multiline
                     className="block text-sm text-[#5a5750] leading-relaxed mb-4 font-light" as="p" />
                  <div className="border-t border-[#e8e0d0] pt-3 mt-3">
                    <p className="text-xs tracking-widest uppercase text-[#7a8a70] mb-1">Docente</p>
                    <T editing={editing} value={p.docente} onChange={(v) => set(`percorsi.items[${i}].docente`, v)}
                       className="block text-base font-semibold text-[#2d3a2e]" as="p" />
                    <T editing={editing} value={p.ruolo} onChange={(v) => set(`percorsi.items[${i}].ruolo`, v)}
                       className="block text-xs text-[#7a8a70] italic mt-1" as="p" />
                  </div>
                  {editing && (
                    <button onClick={() => { const arr = perc.items.filter((_, ii) => ii !== i); set("percorsi.items", arr); }} className="mt-3 text-red-500 hover:text-red-700 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi percorso</button>
                  )}
                </div>
              </div>
            );
          })}
          {editing && (
            <button onClick={() => set("percorsi.items", [...(perc.items || []), { icon: "Leaf", titolo: "Nuovo percorso", desc: "Descrizione", docente: "Docente", ruolo: "Ruolo", img: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80" }])} className="border-2 border-dashed border-[#d4b678] rounded-lg p-8 text-[#7a8a70] hover:bg-[#faf7f2] flex flex-col items-center justify-center min-h-[300px]"><Plus className="w-8 h-8 mb-2" /> Aggiungi percorso</button>
          )}
        </div>
      </section>

      {/* METODO ITA318 */}
      <section className="bg-[#2d3a2e] py-24 px-6 text-center relative" data-testid="metodo-ita318">
        <div className="max-w-4xl mx-auto">
          <T editing={editing} value={ita.eyebrow} onChange={(v) => set("ita318.eyebrow", v)}
             className="block text-[#d4b678] text-xs tracking-[5px] uppercase mb-6" as="p" />
          <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
            <T editing={editing} value={ita.title_pt1} onChange={(v) => set("ita318.title_pt1", v)} as="span" />{" "}
            <em className="text-[#d4b678] not-italic">
              <T editing={editing} value={ita.title_pt2} onChange={(v) => set("ita318.title_pt2", v)} as="span" />
            </em>
          </h2>
          <div className="relative inline-block">
            <img src={ita.logo} alt="ITA318" className="h-24 mx-auto my-8 bg-white rounded-full p-3" />
            {editing && (
              <button onClick={() => uploadImage("ita318.logo")} className="absolute -top-2 -right-2 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-3 py-1 rounded-full font-semibold">Cambia logo</button>
            )}
          </div>
          <T editing={editing} value={ita.description} onChange={(v) => set("ita318.description", v)} multiline
             className="block text-lg md:text-xl text-white/85 leading-[1.9] font-light" as="p" />
        </div>
      </section>

      {/* NUTRIZIONE INTELLIGENTE & FOOD DELIVERY */}
      <section className="bg-white py-24 px-6" data-testid="nutrizione">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="relative rounded-lg overflow-hidden shadow-2xl order-2 md:order-1">
            <img src={nut.image} alt="Nutrizione Smart" className="w-full h-[520px] object-cover" />
            {editing && (
              <button onClick={() => uploadImage("nutrizione.image")} className="absolute top-3 right-3 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-3 py-2 rounded-full font-semibold">Cambia immagine</button>
            )}
          </div>
          <div className="order-1 md:order-2">
            <T editing={editing} value={nut.eyebrow} onChange={(v) => set("nutrizione.eyebrow", v)}
               className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-4" as="p" />
            <T editing={editing} value={nut.title} onChange={(v) => set("nutrizione.title", v)}
               className="block text-4xl md:text-5xl font-light text-[#2d3a2e] mb-4 leading-tight" as="h2" />
            <T editing={editing} value={nut.subtitle} onChange={(v) => set("nutrizione.subtitle", v)} multiline
               className="block text-lg text-[#5a5750] leading-relaxed mb-6 font-light italic" as="p" />
            <T editing={editing} value={nut.intro} onChange={(v) => set("nutrizione.intro", v)}
               className="block text-sm font-semibold text-[#2d3a2e] uppercase tracking-wide mb-3" as="p" />
            <ul className="space-y-2 text-[#5a5750] font-light mb-6">
              {(nut.servizi || []).map((v, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="text-[#d4b678]">✦</span>
                  {editing ? (
                    <div className="flex-1 flex gap-2">
                      <Input value={v} onChange={(e) => { const arr = [...nut.servizi]; arr[i] = e.target.value; set("nutrizione.servizi", arr); }} className="text-sm border-2 border-amber-400" />
                      <button onClick={() => { const arr = nut.servizi.filter((_, ii) => ii !== i); set("nutrizione.servizi", arr); }} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ) : <span>{v}</span>}
                </li>
              ))}
              {editing && <li><button onClick={() => set("nutrizione.servizi", [...(nut.servizi || []), "Nuovo servizio"])} className="text-[#7a8a70] text-xs flex items-center gap-1"><Plus className="w-3 h-3" /> Aggiungi</button></li>}
            </ul>
            <T editing={editing} value={nut.obiettivi_intro} onChange={(v) => set("nutrizione.obiettivi_intro", v)}
               className="block text-sm font-semibold text-[#2d3a2e] uppercase tracking-wide mb-3" as="p" />
            <div className="flex flex-wrap gap-2">
              {(nut.obiettivi || []).map((v, i) => (
                editing ? (
                  <div key={i} className="flex items-center gap-1 bg-[#faf7f2] border-2 border-amber-400 rounded-full px-2">
                    <Input value={v} onChange={(e) => { const arr = [...nut.obiettivi]; arr[i] = e.target.value; set("nutrizione.obiettivi", arr); }} className="text-xs border-0 h-7 w-32" />
                    <button onClick={() => { const arr = nut.obiettivi.filter((_, ii) => ii !== i); set("nutrizione.obiettivi", arr); }} className="text-red-500"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <span key={i} className="inline-block bg-[#faf7f2] border border-[#d4b678] text-[#2d3a2e] text-xs px-3 py-1.5 rounded-full font-medium">{v}</span>
                )
              ))}
              {editing && <button onClick={() => set("nutrizione.obiettivi", [...(nut.obiettivi || []), "Nuovo obiettivo"])} className="text-[#7a8a70] text-xs flex items-center gap-1 border-2 border-dashed border-[#d4b678] rounded-full px-3 py-1"><Plus className="w-3 h-3" /> Aggiungi</button>}
            </div>
          </div>
        </div>

        {/* GRIGLIA ICONE NUTRIZIONE */}
        <div className="max-w-6xl mx-auto mt-20 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="nutrizione-icone">
          {(nut.icone || []).map((it, i) => (
            <div key={i} className="bg-[#faf7f2] rounded-lg p-5 text-center border border-[#e8e0d0] hover:shadow-lg transition-shadow relative">
              {editing ? (
                <div className="space-y-2">
                  <Input value={it.emoji} onChange={(e) => { const arr = [...nut.icone]; arr[i] = { ...it, emoji: e.target.value }; set("nutrizione.icone", arr); }} className="text-3xl text-center border-2 border-amber-400" placeholder="Emoji" />
                  <Textarea value={it.label} onChange={(e) => { const arr = [...nut.icone]; arr[i] = { ...it, label: e.target.value }; set("nutrizione.icone", arr); }} className="text-xs border-2 border-amber-400" rows={2} />
                  <button onClick={() => { const arr = nut.icone.filter((_, ii) => ii !== i); set("nutrizione.icone", arr); }} className="text-red-500 text-xs"><Trash2 className="w-3 h-3 inline" /> Rimuovi</button>
                </div>
              ) : (
                <>
                  <div className="text-4xl mb-2">{it.emoji}</div>
                  <p className="text-xs font-medium text-[#2d3a2e] leading-snug">{it.label}</p>
                </>
              )}
            </div>
          ))}
          {editing && (
            <button onClick={() => set("nutrizione.icone", [...(nut.icone || []), { emoji: "✨", label: "Nuovo servizio" }])} className="bg-white rounded-lg p-5 border-2 border-dashed border-[#d4b678] text-[#7a8a70] text-xs flex flex-col items-center justify-center gap-1"><Plus className="w-6 h-6" /> Aggiungi</button>
          )}
        </div>
      </section>

      {/* METODO BIOFITNESS */}
      <section className="bg-gradient-to-br from-[#faf7f2] via-white to-[#f5eee0] py-24 px-6 relative overflow-hidden" data-testid="biofitness">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <T editing={editing} value={bio.eyebrow} onChange={(v) => set("biofitness.eyebrow", v)}
               className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-4" as="p" />
            <h2 className="text-4xl md:text-6xl font-light text-[#2d3a2e] mb-4 leading-[1.05]">
              <span className="block text-[#d4b678]">
                <T editing={editing} value={bio.title_pt1} onChange={(v) => set("biofitness.title_pt1", v)} as="span" />
              </span>
              <em className="not-italic text-2xl md:text-3xl font-light text-[#5a5750] block mt-2">
                <T editing={editing} value={bio.title_pt2} onChange={(v) => set("biofitness.title_pt2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={bio.intro} onChange={(v) => set("biofitness.intro", v)} multiline
               className="block text-lg text-[#5a5750] leading-[1.8] mb-8 font-light" as="p" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <T editing={editing} value={bio.unisce_title} onChange={(v) => set("biofitness.unisce_title", v)}
                   className="block text-sm font-semibold text-[#2d3a2e] uppercase tracking-wide mb-3" as="p" />
                <ul className="space-y-1.5 text-sm text-[#5a5750] font-light">
                  {(bio.unisce || []).map((v, i) => (
                    <li key={i} className="flex gap-2 items-start">
                      <span className="text-[#d4b678]">◆</span>
                      {editing ? (
                        <div className="flex-1 flex gap-1">
                          <Input value={v} onChange={(e) => { const arr = [...bio.unisce]; arr[i] = e.target.value; set("biofitness.unisce", arr); }} className="text-xs border-2 border-amber-400 h-7" />
                          <button onClick={() => { const arr = bio.unisce.filter((_, ii) => ii !== i); set("biofitness.unisce", arr); }} className="text-red-500"><X className="w-3 h-3" /></button>
                        </div>
                      ) : <span>{v}</span>}
                    </li>
                  ))}
                  {editing && <li><button onClick={() => set("biofitness.unisce", [...(bio.unisce || []), "Nuovo"])} className="text-[#7a8a70] text-xs flex items-center gap-1"><Plus className="w-3 h-3" /> Aggiungi</button></li>}
                </ul>
              </div>
              <div>
                <T editing={editing} value={bio.obiettivi_title} onChange={(v) => set("biofitness.obiettivi_title", v)}
                   className="block text-sm font-semibold text-[#2d3a2e] uppercase tracking-wide mb-3" as="p" />
                <ul className="space-y-1.5 text-sm text-[#5a5750] font-light">
                  {(bio.obiettivi || []).map((v, i) => (
                    <li key={i} className="flex gap-2 items-start">
                      <span className="text-[#d4b678]">◆</span>
                      {editing ? (
                        <div className="flex-1 flex gap-1">
                          <Input value={v} onChange={(e) => { const arr = [...bio.obiettivi]; arr[i] = e.target.value; set("biofitness.obiettivi", arr); }} className="text-xs border-2 border-amber-400 h-7" />
                          <button onClick={() => { const arr = bio.obiettivi.filter((_, ii) => ii !== i); set("biofitness.obiettivi", arr); }} className="text-red-500"><X className="w-3 h-3" /></button>
                        </div>
                      ) : <span>{v}</span>}
                    </li>
                  ))}
                  {editing && <li><button onClick={() => set("biofitness.obiettivi", [...(bio.obiettivi || []), "Nuovo"])} className="text-[#7a8a70] text-xs flex items-center gap-1"><Plus className="w-3 h-3" /> Aggiungi</button></li>}
                </ul>
              </div>
            </div>
          </div>
          <div className="relative rounded-lg overflow-hidden shadow-2xl">
            <img src={bio.image} alt="BioFitness" className="w-full h-[540px] object-cover" />
            {editing && (
              <button onClick={() => uploadImage("biofitness.image")} className="absolute top-3 right-3 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-3 py-2 rounded-full font-semibold">Cambia immagine</button>
            )}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur px-4 py-2 rounded-full text-xs font-semibold text-[#2d3a2e] tracking-wider uppercase shadow-lg">
              Dott. Jacopo Querci
            </div>
          </div>
        </div>
      </section>

      {/* ECOSISTEMA */}
      <section className="bg-[#2d3a2e] py-24 px-6 relative overflow-hidden" data-testid="ecosistema">
        <div className="max-w-6xl mx-auto text-center mb-14">
          <T editing={editing} value={eco.eyebrow} onChange={(v) => set("ecosistema.eyebrow", v)}
             className="block text-[#d4b678] text-xs tracking-[5px] uppercase mb-4" as="p" />
          <T editing={editing} value={eco.title} onChange={(v) => set("ecosistema.title", v)}
             className="block text-4xl md:text-5xl font-light text-white mb-4" as="h2" />
          <T editing={editing} value={eco.subtitle} onChange={(v) => set("ecosistema.subtitle", v)} multiline
             className="block text-lg text-white/80 font-light max-w-2xl mx-auto" as="p" />
        </div>

        {/* GRAFICA CIRCOLARE */}
        <div className="max-w-3xl mx-auto relative aspect-square" data-testid="ecosistema-circle">
          {/* Cerchi decorativi */}
          <div className="absolute inset-0 rounded-full border border-[#d4b678]/30"></div>
          <div className="absolute inset-[15%] rounded-full border border-[#d4b678]/20"></div>
          {/* Centro */}
          <div className="absolute inset-[30%] rounded-full bg-gradient-to-br from-[#d4b678] to-[#c4a668] flex items-center justify-center text-center shadow-2xl border-4 border-white/10">
            <div className="px-4">
              {editing ? (
                <Textarea value={eco.centro || ""} onChange={(e) => set("ecosistema.centro", e.target.value)} className="text-xs text-center bg-white/95 text-slate-900 border-0" rows={3} />
              ) : (
                <p className="text-[#2d3a2e] font-semibold text-sm md:text-base leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{eco.centro}</p>
              )}
            </div>
          </div>
          {/* Orbitali */}
          {(eco.orbitals || []).map((label, i, arr) => {
            const angle = (360 / arr.length) * i - 90;
            const rad = (angle * Math.PI) / 180;
            const radiusPct = 46;
            const x = 50 + radiusPct * Math.cos(rad);
            const y = 50 + radiusPct * Math.sin(rad);
            return (
              <div key={i} className="absolute" style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}>
                <div className="bg-white/95 backdrop-blur rounded-full px-3 py-2 md:px-4 md:py-2.5 shadow-xl border-2 border-[#d4b678] flex items-center gap-1 whitespace-nowrap">
                  {editing ? (
                    <>
                      <Input value={label} onChange={(e) => { const arr2 = [...eco.orbitals]; arr2[i] = e.target.value; set("ecosistema.orbitals", arr2); }} className="text-xs h-6 w-24 border-0 p-1" />
                      <button onClick={() => { const arr2 = eco.orbitals.filter((_, ii) => ii !== i); set("ecosistema.orbitals", arr2); }} className="text-red-500"><X className="w-3 h-3" /></button>
                    </>
                  ) : (
                    <span className="text-[#2d3a2e] text-xs md:text-sm font-semibold">{label}</span>
                  )}
                </div>
                {/* Linea connessione */}
                <svg className="absolute pointer-events-none" style={{ left: "50%", top: "50%", width: "200px", height: "200px", transform: `translate(-50%, -50%) rotate(${angle + 180}deg)`, zIndex: -1 }}>
                  <line x1="100" y1="100" x2="200" y2="100" stroke="#d4b678" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="3,3" />
                </svg>
              </div>
            );
          })}
        </div>
        {editing && (
          <div className="text-center mt-6">
            <button onClick={() => set("ecosistema.orbitals", [...(eco.orbitals || []), "Nuova disciplina"])} className="text-[#d4b678] text-xs flex items-center gap-1 mx-auto border-2 border-dashed border-[#d4b678] rounded-full px-4 py-2"><Plus className="w-4 h-4" /> Aggiungi disciplina orbitale</button>
          </div>
        )}
      </section>

      {/* SERVIZI ESCLUSIVI */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="servizi-esclusivi">
        <div className="text-center mb-16">
          <T editing={editing} value={srv.eyebrow} onChange={(v) => set("servizi_esclusivi.eyebrow", v)}
             className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-4" as="p" />
          <T editing={editing} value={srv.title} onChange={(v) => set("servizi_esclusivi.title", v)}
             className="block text-4xl md:text-5xl font-light text-[#2d3a2e]" as="h2" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(srv.items || []).map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <div key={i} className="bg-gradient-to-br from-white to-[#faf7f2] rounded-xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 border border-[#e8e0d0] group relative" data-testid={`servizio-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#d4b678]/15 flex items-center justify-center mb-5 group-hover:bg-[#d4b678]/25 transition-colors">
                  <Icon className="w-7 h-7 text-[#d4b678]" strokeWidth={1.4} />
                </div>
                <T editing={editing} value={s.titolo} onChange={(v) => { const arr = [...srv.items]; arr[i] = { ...s, titolo: v }; set("servizi_esclusivi.items", arr); }}
                   className="block text-xl md:text-2xl font-normal text-[#2d3a2e] mb-3 leading-tight" as="h3" />
                <T editing={editing} value={s.desc} onChange={(v) => { const arr = [...srv.items]; arr[i] = { ...s, desc: v }; set("servizi_esclusivi.items", arr); }} multiline
                   className="block text-sm text-[#5a5750] leading-relaxed font-light" as="p" />
                {editing && (
                  <div className="mt-3 space-y-2">
                    <select value={s.icon} onChange={(e) => { const arr = [...srv.items]; arr[i] = { ...s, icon: e.target.value }; set("servizi_esclusivi.items", arr); }} className="text-xs border rounded px-2 py-1 w-full">
                      {Object.keys(ICONS).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                    <button onClick={() => { const arr = srv.items.filter((_, ii) => ii !== i); set("servizi_esclusivi.items", arr); }} className="text-red-500 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi</button>
                  </div>
                )}
              </div>
            );
          })}
          {editing && (
            <button onClick={() => set("servizi_esclusivi.items", [...(srv.items || []), { titolo: "Nuovo servizio", desc: "Descrizione", icon: "Sparkles" }])} className="border-2 border-dashed border-[#d4b678] rounded-xl p-8 text-[#7a8a70] hover:bg-[#faf7f2] flex flex-col items-center justify-center min-h-[240px]"><Plus className="w-8 h-8 mb-2" /> Aggiungi servizio</button>
          )}
        </div>
      </section>

      {/* PRODOTTI ODR */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-12 items-center" data-testid="prodotti">
        <div>
          <T editing={editing} value={prod.eyebrow} onChange={(v) => set("prodotti.eyebrow", v)}
             className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-4" as="p" />
          <h2 className="text-4xl md:text-5xl font-light text-[#2d3a2e] mb-6 leading-tight">
            <T editing={editing} value={prod.title_pt1} onChange={(v) => set("prodotti.title_pt1", v)} as="span" />{" "}
            <em className="text-[#7a8a70] not-italic">
              <T editing={editing} value={prod.title_pt2} onChange={(v) => set("prodotti.title_pt2", v)} as="span" />
            </em>
          </h2>
          <T editing={editing} value={prod.description} onChange={(v) => set("prodotti.description", v)} multiline
             className="block text-lg text-[#5a5750] leading-[1.9] font-light mb-6" as="p" />
          <ul className="space-y-3 text-[#5a5750] font-light">
            {(prod.punti || []).map((v, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="text-[#d4b678] text-lg">✦</span>
                {editing ? (
                  <div className="flex-1 flex gap-2">
                    <Input value={v} onChange={(e) => { const arr = [...prod.punti]; arr[i] = e.target.value; set("prodotti.punti", arr); }} className="bg-white text-slate-900 border-2 border-amber-400 text-sm" />
                    <button onClick={() => { const arr = prod.punti.filter((_, ii) => ii !== i); set("prodotti.punti", arr); }} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ) : <span>{v}</span>}
              </li>
            ))}
            {editing && (
              <li><button onClick={() => set("prodotti.punti", [...(prod.punti || []), "Nuovo punto"])} className="text-[#7a8a70] text-xs flex items-center gap-1 hover:text-[#2d3a2e]"><Plus className="w-3 h-3" /> Aggiungi punto</button></li>
            )}
          </ul>
          <div className="mt-8">
            {editing ? (
              <div className="space-y-2">
                <Input value={prod.cta_label || ""} onChange={(e) => set("prodotti.cta_label", e.target.value)} placeholder="Etichetta CTA" className="bg-white text-slate-900 border-2 border-amber-400" />
                <Input value={prod.cta_link || ""} onChange={(e) => set("prodotti.cta_link", e.target.value)} placeholder="Link CTA" className="bg-white text-slate-900 border-2 border-amber-400" />
              </div>
            ) : (
              <Link to={prod.cta_link || "/"} className="inline-flex items-center gap-2 text-[#2d3a2e] font-semibold border-b-2 border-[#d4b678] pb-1 hover:text-[#7a8a70] transition-colors">
                {prod.cta_label} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
        <div className="rounded-lg overflow-hidden shadow-2xl relative">
          <img src={prod.image} alt="Prodotti ODR" className="w-full h-[500px] object-cover" />
          {editing && (
            <button onClick={() => uploadImage("prodotti.image")} className="absolute top-2 right-2 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-3 py-2 rounded-full font-semibold">Cambia immagine</button>
          )}
        </div>
      </section>

      {/* RETREAT */}
      <section className="relative h-[500px] w-full overflow-hidden" data-testid="retreat">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${ret.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a2e20]/90 via-[#2d3a2e]/50 to-transparent" />
        {editing && (
          <button onClick={() => uploadImage("retreat.image")} className="absolute top-4 right-4 z-20 bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs px-3 py-2 rounded-full font-semibold">Cambia immagine retreat</button>
        )}
        <div className="relative z-10 h-full flex items-end pb-16 px-8 md:px-16 lg:px-24">
          <div className="max-w-3xl w-full">
            <T editing={editing} value={ret.eyebrow} onChange={(v) => set("retreat.eyebrow", v)}
               className="block text-[#d4b678] text-xs tracking-[5px] uppercase mb-4" as="p" />
            <h2 className="text-white text-4xl md:text-5xl font-light mb-4 leading-tight">
              <T editing={editing} value={ret.title_pt1} onChange={(v) => set("retreat.title_pt1", v)} as="span" />{" "}
              <em className="text-[#d4b678] not-italic">
                <T editing={editing} value={ret.title_pt2} onChange={(v) => set("retreat.title_pt2", v)} as="span" />
              </em>
            </h2>
            <T editing={editing} value={ret.description} onChange={(v) => set("retreat.description", v)} multiline
               className="block text-white/90 text-lg font-light" as="p" />
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section id="contatti" className="bg-[#faf7f2] py-24 px-6 text-center" data-testid="cta-finale">
        <div className="max-w-3xl mx-auto">
          <T editing={editing} value={cta.eyebrow} onChange={(v) => set("cta_finale.eyebrow", v)}
             className="block text-[#7a8a70] text-xs tracking-[5px] uppercase mb-6" as="p" />
          <h2 className="text-4xl md:text-6xl font-light text-[#2d3a2e] mb-6 leading-tight">
            <T editing={editing} value={cta.title_pt1} onChange={(v) => set("cta_finale.title_pt1", v)} as="span" />{" "}
            <em className="text-[#d4b678] not-italic">
              <T editing={editing} value={cta.title_pt2} onChange={(v) => set("cta_finale.title_pt2", v)} as="span" />
            </em>
          </h2>
          <T editing={editing} value={cta.subtitle} onChange={(v) => set("cta_finale.subtitle", v)}
             className="block text-lg text-[#5a5750] mb-10 font-light" as="p" />
          <div className="flex flex-wrap gap-3 justify-center">
            {(cta.buttons || []).map((b, i) => {
              const styles = {
                dark: "bg-[#2d3a2e] hover:bg-[#1a2e20] text-white",
                gold: "bg-[#d4b678] hover:bg-[#c4a668] text-[#2d3a2e]",
                outline: "border-2 border-[#2d3a2e] hover:bg-[#2d3a2e] hover:text-white text-[#2d3a2e]",
              };
              if (editing) {
                return (
                  <div key={i} className="flex flex-col gap-1 p-3 border-2 border-amber-300 rounded bg-white">
                    <Input value={b.label} onChange={(e) => { const arr = [...cta.buttons]; arr[i] = { ...b, label: e.target.value }; set("cta_finale.buttons", arr); }} placeholder="Etichetta" className="text-sm" />
                    <Input value={b.email} onChange={(e) => { const arr = [...cta.buttons]; arr[i] = { ...b, email: e.target.value }; set("cta_finale.buttons", arr); }} placeholder="Email" className="text-sm" />
                    <Input value={b.subject} onChange={(e) => { const arr = [...cta.buttons]; arr[i] = { ...b, subject: e.target.value }; set("cta_finale.buttons", arr); }} placeholder="Oggetto" className="text-sm" />
                    <select value={b.variant} onChange={(e) => { const arr = [...cta.buttons]; arr[i] = { ...b, variant: e.target.value }; set("cta_finale.buttons", arr); }} className="text-sm border rounded px-2 py-1">
                      <option value="dark">Scuro</option>
                      <option value="gold">Oro</option>
                      <option value="outline">Outline</option>
                    </select>
                    <button onClick={() => { const arr = cta.buttons.filter((_, ii) => ii !== i); set("cta_finale.buttons", arr); }} className="text-red-500 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> Rimuovi</button>
                  </div>
                );
              }
              return (
                <a key={i} href={`mailto:${b.email}?subject=${encodeURIComponent(b.subject || "")}`} className={`inline-flex items-center gap-2 ${styles[b.variant] || styles.dark} px-8 py-3.5 rounded-full font-semibold text-sm tracking-wider uppercase transition-colors`} data-testid={`cta-btn-${i}`}>
                  {b.label}
                </a>
              );
            })}
            {editing && (
              <button onClick={() => set("cta_finale.buttons", [...(cta.buttons || []), { label: "Nuovo pulsante", email: "info@ita318.it", subject: "", variant: "dark" }])} className="border-2 border-dashed border-[#d4b678] rounded p-3 text-[#7a8a70] text-sm flex items-center gap-1"><Plus className="w-4 h-4" /> Aggiungi pulsante</button>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#2d3a2e] py-10 px-6 text-center text-white/70 text-sm">
        <T editing={editing} value={foot.text} onChange={(v) => set("footer.text", v)}
           className="block tracking-wider" as="p" />
        <p className="mt-2 text-xs">
          {editing ? (
            <Input value={foot.email || ""} onChange={(e) => set("footer.email", e.target.value)} className="bg-white/95 text-slate-900 max-w-xs mx-auto text-center border-2 border-amber-400" />
          ) : (
            <a href={`mailto:${foot.email}`} className="text-[#d4b678] hover:underline">{foot.email}</a>
          )}
        </p>
      </footer>

      {/* WhatsApp Floating */}
      {!editing && wa.number && (
        <a href={`https://wa.me/${wa.number}?text=${encodeURIComponent(wa.message || "")}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] hover:scale-110 transition-transform shadow-2xl flex items-center justify-center" data-testid="wa-float" title="Contattaci su WhatsApp">
          <MessageCircle className="w-7 h-7 text-white" />
        </a>
      )}
      {editing && (
        <div className="fixed bottom-6 right-6 z-50 bg-white rounded-lg shadow-2xl p-3 border-2 border-[#d4b678] max-w-xs">
          <p className="text-xs font-semibold text-[#2d3a2e] mb-2">WhatsApp Floating</p>
          <Input value={wa.number || ""} onChange={(e) => set("whatsapp.number", e.target.value)} placeholder="Numero (es. 393000000000)" className="text-xs mb-1" />
          <Input value={wa.message || ""} onChange={(e) => set("whatsapp.message", e.target.value)} placeholder="Messaggio predefinito" className="text-xs" />
        </div>
      )}
    </div>
  );
};

export default AcademyBenessereLandingPage;
